import React, { useRef, useState } from "react";
import { Star, X, Upload, Loader2, ShieldCheck } from "lucide-react";
import api, { fileUrl } from "@/lib/api";
import { toast } from "sonner";

export function Avatar({ src, name, size = 40, admin = false }) {
  const initials = (name || "?").trim().slice(0, 1).toUpperCase();
  return (
    <div className="relative shrink-0" style={{ width: size, height: size }}>
      {src ? (
        <img
          src={fileUrl(src)}
          alt={name}
          className="w-full h-full object-cover rounded-full border-2 border-black"
          style={{ width: size, height: size }}
        />
      ) : (
        <div
          className="rounded-full border-2 border-black flex items-center justify-center font-display font-bold"
          style={{ width: size, height: size, background: "#C7A656", color: "#121212", fontSize: size * 0.42 }}
        >
          {initials}
        </div>
      )}
      {admin && (
        <div className="absolute -bottom-1 -right-1 bg-[#8B1E28] border-2 border-black rounded-full p-[2px]">
          <ShieldCheck size={size * 0.32} color="#F4F0E6" />
        </div>
      )}
    </div>
  );
}

export function Stars({ value = 0, size = 16, onChange }) {
  return (
    <div className="flex gap-[2px]">
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          key={n}
          type="button"
          disabled={!onChange}
          data-testid={onChange ? `star-${n}` : undefined}
          onClick={() => onChange && onChange(n)}
          className={onChange ? "cursor-pointer" : "cursor-default"}
        >
          <Star size={size} strokeWidth={2} fill={n <= value ? "#C7A656" : "none"} color="#121212" />
        </button>
      ))}
    </div>
  );
}

export function Modal({ open, onClose, children, title, testid }) {
  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/70 p-0 sm:p-4"
      onClick={onClose}
      data-testid={testid ? `${testid}-overlay` : undefined}
    >
      <div
        className="phone-frame-modal w-full max-w-[440px] bg-[#F4F0E6] border-2 border-black rounded-t-xl sm:rounded-xl max-h-[92vh] overflow-y-auto no-scrollbar rise-in"
        onClick={(e) => e.stopPropagation()}
        data-testid={testid}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b-2 border-black sticky top-0 bg-[#EAE4D3] z-10">
          <h3 className="font-display font-bold text-lg uppercase tracking-wide">{title}</h3>
          <button onClick={onClose} data-testid={testid ? `${testid}-close` : "modal-close"} className="p-1">
            <X size={22} />
          </button>
        </div>
        <div className="p-5">{children}</div>
      </div>
    </div>
  );
}

export function Uploader({ onUploaded, label = "Upload photo", testid = "uploader" }) {
  const inputRef = useRef();
  const [busy, setBusy] = useState(false);
  const handle = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onUploaded(data.url);
      toast.success("Uploaded");
    } catch (err) {
      toast.error("Upload failed");
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };
  return (
    <>
      <button
        type="button"
        className="atc-btn btn-outline w-full text-sm"
        onClick={() => inputRef.current?.click()}
        disabled={busy}
        data-testid={`${testid}-btn`}
      >
        {busy ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />}
        {busy ? "Uploading..." : label}
      </button>
      <input ref={inputRef} type="file" accept="image/*" className="hidden" onChange={handle} data-testid={`${testid}-input`} />
    </>
  );
}

export function Loading({ label = "Loading" }) {
  return (
    <div className="flex flex-col items-center justify-center py-16 gap-3 text-[#554E40]">
      <Loader2 className="animate-spin" size={28} />
      <span className="font-mono2 text-xs uppercase tracking-widest">{label}</span>
    </div>
  );
}

export function Empty({ title, sub }) {
  return (
    <div className="text-center py-16 px-6">
      <p className="font-display font-bold text-xl">{title}</p>
      {sub && <p className="text-sm text-[#554E40] mt-2">{sub}</p>}
    </div>
  );
}

export function Chips({ options, value, onChange, testid = "chip" }) {
  return (
    <div className="flex gap-2 overflow-x-auto no-scrollbar px-4 py-3">
      {options.map((o) => (
        <button
          key={o}
          className={`chip ${value === o ? "chip-active" : ""}`}
          onClick={() => onChange(o)}
          data-testid={`${testid}-${o.replace(/\s+/g, "-").toLowerCase()}`}
        >
          {o}
        </button>
      ))}
    </div>
  );
}
